/**
 * Fee Collection Service
 *
 * Generalizable fee collection (e.g. registration/check-in fee) with Stripe and Paystack.
 * - getFeeConfig / getFeeForType: read clinic fee config from metadata
 * - initiateFeePayment: call Cloud Function to create payment and start provider flow
 * - getPaymentStatus: call Cloud Function to poll payment status (payer or clinic)
 */

import { getClinicMetadata } from './checkInFormService';
import { functions } from './firebase';

const CALLABLE_TIMEOUT_MS = 60_000;

/**
 * Get full fee config for a clinic
 * @param {string} clinicId - Clinic ID
 * @returns {Promise<Object|null>} feeConfig object { [feeType]: { amountKsh, currency, enabled, label, provider } } or null
 */
export async function getFeeConfig(clinicId) {
  if (!clinicId) {
    console.warn('[feeCollectionService] getFeeConfig: no clinicId');
    return null;
  }
  const metadata = await getClinicMetadata(clinicId);
  return metadata?.feeConfig ?? null;
}

/**
 * Get config for a single fee type (e.g. 'registration')
 * @param {string} clinicId - Clinic ID
 * @param {string} feeType - Fee type key (e.g. 'registration')
 * @returns {Promise<Object|null>} { amountKsh, currency, enabled, label, provider } or null
 */
export async function getFeeForType(clinicId, feeType) {
  const config = await getFeeConfig(clinicId);
  if (!config || !feeType) return null;
  const fee = config[feeType];
  return fee && typeof fee === 'object' ? fee : null;
}

/**
 * Initiate a fee payment (Stripe or Paystack). Calls Cloud Function; provider is chosen from clinic feeConfig.
 * @param {Object} params
 * @param {string} params.clinicId - Clinic ID
 * @param {string} params.feeType - Fee type (e.g. 'registration')
 * @param {string} [params.phoneNumber] - Required for Paystack (M-Pesa); optional for Stripe
 * @param {string} params.referenceType - e.g. 'checkin_session'
 * @param {string} params.referenceId - e.g. session id for idempotency
 * @param {Object} [params.metadata] - Optional extra payload
 * @returns {Promise<{ paymentId: string, providerRequestId?: string, clientSecret?: string, status: string }>}
 */
export async function initiateFeePayment({
  clinicId,
  feeType,
  phoneNumber,
  referenceType,
  referenceId,
  metadata = {},
}) {
  if (!clinicId || !feeType || !referenceType || !referenceId) {
    throw new Error('feeCollectionService.initiateFeePayment: clinicId, feeType, referenceType, referenceId required');
  }
  const callable = functions.httpsCallable('initiateFeePayment', { timeout: CALLABLE_TIMEOUT_MS });
  const { data } = await callable({
    clinicId,
    feeType,
    phoneNumber: phoneNumber || null,
    referenceType,
    referenceId,
    metadata,
  });
  return data;
}

/**
 * Get status of a fee payment (for polling). Callable enforces payer or clinic role.
 * @param {string} paymentId - feePayments document ID
 * @returns {Promise<{ status: string, providerReference?: string }>}
 */
export async function getPaymentStatus(paymentId) {
  if (!paymentId) {
    throw new Error('feeCollectionService.getPaymentStatus: paymentId required');
  }
  const callable = functions.httpsCallable('getPaymentStatus', { timeout: 15_000 });
  const { data } = await callable({ paymentId });
  return data;
}

const feeCollectionService = {
  getFeeConfig,
  getFeeForType,
  initiateFeePayment,
  getPaymentStatus,
};

export default feeCollectionService;
